import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { monitoringConfig } from '../config/monitoring';
import { PhoneConnectionStatus } from '../context/PhonePairingContext';
import { useExam } from '../context/ExamContext';
import { Observation, ObservationSource, ObservationType } from '../types/domain';
import { createSceneSignature, frameDifference, frameHealthConditions, readMonitoringFrame } from '../utils/monitoringFrames';
import { ObservationCondition } from '../utils/observations';
import { monitoringIsInGrace } from '../utils/monitoringSignals';
import { CandidateWarning } from '../utils/browserWarnings';
import { monitoringNoticeDecision, observationSourceLabel } from '../utils/monitoringNotices';
import { MobileMovementDetector } from '../utils/mobileMovement';
import { useWarningAudio } from './useWarningAudio';
import { useCandidatePresence } from './useCandidatePresence';

interface ExamMonitoringOptions {
  enabled: boolean;
  laptopVideoRef: RefObject<HTMLVideoElement>;
  mobileVideoRef: RefObject<HTMLVideoElement>;
  laptopStream?: MediaStream;
  mobileStream?: MediaStream;
  phoneStatus: PhoneConnectionStatus;
  phoneCameraSwitching?: boolean;
  mobileRequired: boolean;
}

export function useExamMonitoring(options: ExamMonitoringOptions) {
  const { state, recordObservationCondition } = useExam();
  const { play } = useWarningAudio();
  const [latestNotice, setLatestNotice] = useState<CandidateWarning>();
  const presence = useCandidatePresence({
    enabled: options.enabled,
    videoRef: options.laptopVideoRef,
    stream: options.laptopStream,
    record: recordObservationCondition
  });
  const sessionStartedAt = useRef(0);
  const laptopCanvas = useRef<HTMLCanvasElement>();
  const mobileCanvas = useRef<HTMLCanvasElement>();
  const previousLaptop = useRef<Uint8ClampedArray>();
  const previousMobile = useRef<Uint8ClampedArray>();
  const movementDetector = useRef<MobileMovementDetector>();
  const previousMobileStream = useRef<MediaStream>();
  const previousPhoneStatus = useRef<PhoneConnectionStatus>();
  const switchGraceUntil = useRef(0);
  const everConnected = useRef(false);
  const warnedStatuses = useRef(new Map<string, string>());
  const noticeTimer = useRef<number>();
  const lastMonitoringWarningAt = useRef(0);

  useEffect(() => {
    if (!options.enabled) {
      sessionStartedAt.current = 0;
      previousLaptop.current = undefined;
      previousMobile.current = undefined;
      movementDetector.current?.reset();
      return;
    }
    sessionStartedAt.current ||= Date.now();
    laptopCanvas.current ??= document.createElement('canvas');
    mobileCanvas.current ??= document.createElement('canvas');
    movementDetector.current ??= new MobileMovementDetector(monitoringConfig.cameraMovement);
    let cancelled = false;

    const record = (condition: ObservationCondition) => recordObservationCondition(condition);
    const monitorCamera = (source: 'laptop-camera' | 'mobile-camera', video: HTMLVideoElement | null, stream: MediaStream | undefined, now: number) => {
      const prefix = source === 'laptop-camera' ? 'laptop-camera' : 'mobile-camera';
      const track = stream?.getVideoTracks()[0];
      const sample = video && (source === 'laptop-camera' ? laptopCanvas.current : mobileCanvas.current)
        ? readMonitoringFrame(video, source === 'laptop-camera' ? laptopCanvas.current! : mobileCanvas.current!, monitoringConfig.cameraHealth.sampleWidthPx, monitoringConfig.cameraHealth.sampleHeightPx)
        : undefined;
      const inGrace = monitoringIsInGrace(now, sessionStartedAt.current, switchGraceUntil.current, monitoringConfig.cameraHealth.startupGracePeriodMs);
      const disconnected = !inGrace && (!track || track.readyState !== 'live' || track.muted || !sample);
      record({
        type: `${prefix}-disconnected` as ObservationType,
        source,
        present: disconnected,
        confirmationMs: monitoringConfig.connection.disconnectGracePeriodMs,
        recoveryMs: monitoringConfig.connection.reconnectConfirmationMs,
        importance: 'important',
        message: `${observationSourceLabel(source)} is unavailable. Check the camera connection.`,
        technicalDetails: { trackState: track?.readyState ?? 'missing', muted: track?.muted ?? false, dimensionsReady: Boolean(sample) },
        now
      });
      if (!sample || inGrace) return;

      const previous = source === 'laptop-camera' ? previousLaptop : previousMobile;
      const difference = frameDifference(previous.current, sample.pixels);
      previous.current = sample.pixels;
      const health = frameHealthConditions(sample, monitoringConfig.cameraHealth);
      record({ type: `${prefix}-frozen` as ObservationType, source, present: difference <= monitoringConfig.cameraHealth.frozenFrameDifferenceThreshold, confirmationMs: monitoringConfig.cameraHealth.frozenFrameConfirmationMs, recoveryMs: monitoringConfig.cameraHealth.recoveryConfirmationMs, message: `${observationSourceLabel(source)} video may be frozen. Move slightly or restart the camera.`, technicalDetails: { frameDifference: Math.round(difference * 10) / 10 }, now });
      record({ type: `${prefix}-dark` as ObservationType, source, present: health.dark, confirmationMs: monitoringConfig.cameraHealth.darkFrameConfirmationMs, recoveryMs: monitoringConfig.cameraHealth.recoveryConfirmationMs, message: `${observationSourceLabel(source)} view is too dark to monitor clearly.`, technicalDetails: { luminance: Math.round(sample.luminance) }, now });
      record({ type: `${prefix}-overexposed` as ObservationType, source, present: health.overexposed, confirmationMs: monitoringConfig.cameraHealth.brightFrameConfirmationMs, recoveryMs: monitoringConfig.cameraHealth.recoveryConfirmationMs, message: `${observationSourceLabel(source)} view is too bright to monitor clearly.`, technicalDetails: { luminance: Math.round(sample.luminance) }, now });
      record({ type: `${prefix}-obstructed` as ObservationType, source, present: health.obstructed, confirmationMs: monitoringConfig.cameraHealth.obstructionConfirmationMs, recoveryMs: monitoringConfig.cameraHealth.recoveryConfirmationMs, importance: 'important', message: `${observationSourceLabel(source)} view is too dark or may be obstructed.`, technicalDetails: { luminance: Math.round(sample.luminance), variance: Math.round(sample.variance) }, now });

      if (source === 'mobile-camera') {
        const signature = createSceneSignature(sample, monitoringConfig.cameraHealth.sampleWidthPx, monitoringConfig.cameraHealth.sampleHeightPx);
        const movement = movementDetector.current!.update(signature, now);
        record({
          type: 'mobile-camera-moved', source, present: movement.moved, confirmationMs: 0, recoveryMs: 0,
          importance: 'important', message: 'Secondary camera position changed. Restore the required exam view.',
          technicalDetails: {
            movementScore: Math.round(movement.score * 10) / 10,
            structuralScore: Math.round(movement.structural * 10) / 10,
            phase: movement.phase,
            baselineReady: movement.baselineReady
          }, now
        });
      }
    };

    const sample = async () => {
      if (cancelled || document.hidden) return;
      const now = Date.now();
      if (options.phoneStatus === 'connected') everConnected.current = true;
      const reconnected = previousPhoneStatus.current !== undefined && previousPhoneStatus.current !== 'connected' && options.phoneStatus === 'connected';
      if (options.mobileStream !== previousMobileStream.current || options.phoneCameraSwitching || reconnected) {
        previousMobileStream.current = options.mobileStream;
        const graceMs = reconnected ? monitoringConfig.cameraMovement.reconnectGraceMs : monitoringConfig.cameraMovement.cameraSwitchGraceMs;
        switchGraceUntil.current = now + graceMs;
        previousMobile.current = undefined;
        movementDetector.current?.reset(switchGraceUntil.current);
      }
      previousPhoneStatus.current = options.phoneStatus;
      monitorCamera('laptop-camera', options.laptopVideoRef.current, options.laptopStream, now);
      if (options.mobileStream || everConnected.current) monitorCamera('mobile-camera', options.mobileVideoRef.current, options.mobileStream, now);

      const connectionRelevant = everConnected.current || options.mobileRequired;
      const connectionInterrupted = connectionRelevant && ['reconnecting', 'disconnected', 'failed'].includes(options.phoneStatus);
      record({ type: 'connection-degraded', source: 'network', present: connectionInterrupted && now >= switchGraceUntil.current, confirmationMs: monitoringConfig.connection.degradedStateTimeoutMs, recoveryMs: monitoringConfig.connection.reconnectConfirmationMs, importance: 'important', message: 'Secondary camera connection was interrupted. Attempting to reconnect.', technicalDetails: { state: options.phoneStatus }, now });

    };

    void sample();
    const timer = window.setInterval(() => void sample(), monitoringConfig.cameraHealth.sampleIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      laptopCanvas.current = undefined;
      mobileCanvas.current = undefined;
      previousLaptop.current = undefined;
      previousMobile.current = undefined;
      movementDetector.current?.reset();
    };
  }, [options.enabled, options.laptopStream, options.laptopVideoRef, options.mobileRequired, options.mobileStream, options.mobileVideoRef, options.phoneCameraSwitching, options.phoneStatus, recordObservationCondition]);

  useEffect(() => {
    if (!options.enabled || !navigator.permissions?.query) return;
    let permission: PermissionStatus | undefined;
    let cancelled = false;
    const update = () => {
      if (cancelled || !permission) return;
      recordObservationCondition({
        type: 'camera-permission-revoked',
        source: 'laptop-camera',
        present: permission.state === 'denied',
        confirmationMs: 0,
        recoveryMs: 0,
        importance: 'important',
        message: 'Laptop camera permission is unavailable. Restore camera permission to continue monitoring.',
        technicalDetails: { permissionState: permission.state }
      });
    };
    void navigator.permissions.query({ name: 'camera' as PermissionName }).then((result) => {
      permission = result;
      update();
      result.addEventListener('change', update);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
      permission?.removeEventListener('change', update);
    };
  }, [options.enabled, recordObservationCondition]);

  useEffect(() => {
    if (!options.enabled) return;
    const latest = state.observations
      .filter((item) => item.status === 'active' || item.status === 'recovered')
      .sort((a, b) => Date.parse(b.lastUpdatedAt) - Date.parse(a.lastUpdatedAt))[0];
    if (!latest) return;
    const priorStatus = warnedStatuses.current.get(latest.id);
    if (priorStatus === latest.status) return;
    warnedStatuses.current.set(latest.id, latest.status);
    if (latest.status === 'recovered') {
      setLatestNotice(undefined);
      return;
    }
    const now = Date.now();
    const decision = monitoringNoticeDecision(latest, priorStatus as Observation['status'] | undefined, lastMonitoringWarningAt.current, now, monitoringConfig.warnings.cooldownMs);
    if (!decision.notice) return;
    setLatestNotice(decision.notice);
    if (decision.playAudio) {
      lastMonitoringWarningAt.current = now;
      play();
    }
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setLatestNotice(undefined), monitoringConfig.warnings.cooldownMs);
  }, [options.enabled, play, state.observations]);

  useEffect(() => () => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    warnedStatuses.current.clear();
  }, []);

  const active = useMemo(() => state.observations.filter((item) => item.status === 'active'), [state.observations]);
  return {
    observations: state.observations,
    active,
    latestNotice,
    presenceCapability: presence.presenceCapability,
    presenceState: presence.presenceState,
    presenceDiagnostics: presence.presenceDiagnostics,
    statusFor(source: ObservationSource): Observation['status'] | 'ready' {
      return active.some((item) => item.source === source) ? 'active' : 'ready';
    }
  };
}
